// StaffFirst ------ People Management Platform
// React app for Hostinger deployment
// Requires: react, papaparse (npm install papaparse)

import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';

// CONSTANTS & PRELOADED DATA
const CAT_COLORS = {
  'Professional Soft Skills':           '#0ea5e9',
  'Personal Effectiveness':             '#14b8a6',
  'Business Acumen':                    '#8b5cf6',
  'Collaborating with Others':          '#6366f1',
  'Role-Specific Technical/Functional': '#3b82f6',
  'Leadership Soft Skills':             '#f97316',
  'Managing People':                    '#f59e0b',
  'Managing Teams':                     '#10b981',
  'Department Leadership':              '#059669',
  'Leading Division':                   '#1d4ed8',
  'Business Results':                   '#7c3aed',
  'Client Community':                   '#0f766e',
  'Custom':                             '#ec4899',
};

const getCatColors = (customCategories=[]) => {
  const merged = { ...CAT_COLORS };
  (customCategories||[]).forEach(c => { if(c.name) merged[c.name] = c.color; });
  return merged;
};

const PRELOADED_SKILLS = [
  // -- LEADING SELF - Professional Soft Skills
  { id:'sk001', name:'Computer Literacy',                    category:'Professional Soft Skills',           description:'Effectively using computers, software, and digital tools' },
  { id:'sk002', name:'Executive Presence & Professionalism', category:'Professional Soft Skills',           description:'Projecting confidence, credibility, and a professional image' },
  { id:'sk003', name:'Oral and Written Communication',       category:'Professional Soft Skills',           description:'Communicating clearly and effectively in speech and writing' },
  { id:'sk004', name:'Organizational Skills',                category:'Professional Soft Skills',           description:'Managing tasks, priorities, and resources efficiently' },
  { id:'sk005', name:'Presentation Skills',                  category:'Professional Soft Skills',           description:'Delivering compelling and well-structured presentations' },
  // -- LEADING SELF - Personal Effectiveness------------
  { id:'sk006', name:'Accountability',                       category:'Personal Effectiveness',             description:'Taking ownership of actions, decisions, and results' },
  { id:'sk007', name:'Adaptability',                         category:'Personal Effectiveness',             description:'Adjusting effectively to change, ambiguity, and new challenges' },
  { id:'sk008', name:'Emotional Intelligence (EQ)',          category:'Personal Effectiveness',             description:'Understanding and managing emotions in oneself and others' },
  { id:'sk009', name:'Initiative',                           category:'Personal Effectiveness',             description:'Proactively taking action without being asked' },
  { id:'sk010', name:'Resilience & Stress Management',       category:'Personal Effectiveness',             description:'Maintaining effectiveness under pressure and bouncing back from setbacks' },
  { id:'sk011', name:'Growth Mindset',                       category:'Personal Effectiveness',             description:'Embracing challenges and viewing effort as a path to mastery' },
  { id:'sk012', name:'Time Management',                      category:'Personal Effectiveness',             description:'Organizing and prioritizing time to maximize productivity' },
  // -- LEADING OTHERS - Business Acumen------------------------------------------
  { id:'sk013', name:'Action-Oriented / Results-Driven',     category:'Business Acumen',                    description:'Focusing on achieving outcomes with urgency and purpose' },
  { id:'sk014', name:'Agility',                              category:'Business Acumen',                    description:'Moving quickly and decisively in a fast-changing environment' },
  { id:'sk015', name:'Critical Thinking',                    category:'Business Acumen',                    description:'Analyzing situations logically to form sound judgments' },
  { id:'sk016', name:'Decision-Making',                      category:'Business Acumen',                    description:'Making timely, well-informed decisions under uncertainty' },
  { id:'sk017', name:'Entrepreneurial Mindset',              category:'Business Acumen',                    description:'Identifying opportunities and driving innovation' },
  { id:'sk018', name:'Good Judgment',                        category:'Business Acumen',                    description:'Demonstrating sound reasoning and wise choices' },
  { id:'sk019', name:'Intelligent Risk-Taking',              category:'Business Acumen',                    description:'Assessing and taking calculated risks to drive progress' },
  { id:'sk020', name:'Project Management',                   category:'Business Acumen',                    description:'Planning, executing, and completing projects on time' },
  { id:'sk021', name:'Strategic Thinking',                   category:'Business Acumen',                    description:'Connecting day-to-day work to long-term organizational goals' },
  { id:'sk022', name:'Subject Matter Expertise (SME)',       category:'Business Acumen',                    description:'Deep knowledge and authority in a specific domain' },
  // -- LEADING OTHERS - Collaborating with Others ------------------------------------------------------------------------------------------------------------------------------------------------------------------
  { id:'sk023', name:'Active Listening',                     category:'Collaborating with Others',          description:'Fully focusing on and understanding what others are communicating' },
  { id:'sk024', name:'Build Rapport',                        category:'Collaborating with Others',          description:'Establishing trust and positive relationships with colleagues' },
  { id:'sk025', name:'Deal with Difficult People',           category:'Collaborating with Others',          description:'Navigating challenging interpersonal dynamics professionally' },
  { id:'sk026', name:'Demonstrate Empathy',                  category:'Collaborating with Others',          description:'Understanding and sharing the feelings of others' },
  { id:'sk027', name:'Collaborate for Effective Outcomes',   category:'Collaborating with Others',          description:'Working jointly with others to achieve shared goals' },
  { id:'sk028', name:'Communicate Across Diverse Styles',    category:'Collaborating with Others',          description:'Adapting communication to different personalities and backgrounds' },
  { id:'sk029', name:'Influence Without Authority',          category:'Collaborating with Others',          description:'Persuading and inspiring others without formal power' },
  { id:'sk030', name:'Manage Expectations',                  category:'Collaborating with Others',          description:'Setting and maintaining realistic expectations with stakeholders' },
  { id:'sk031', name:'Motivate and Encourage Others',        category:'Collaborating with Others',          description:'Inspiring colleagues to perform at their best' },
  // -- LEADING OTHERS - Role-Specific Technical/Functional ------------------------------------------------------------------------------------------------------------
  { id:'sk032', name:'Program Design, Development & Management', category:'Role-Specific Technical/Functional', description:'Creating, building, and overseeing effective programs' },
  { id:'sk033', name:'Policy',                               category:'Role-Specific Technical/Functional', description:'Developing and implementing organizational policies' },
  { id:'sk034', name:'Technology',                           category:'Role-Specific Technical/Functional', description:'Leveraging technology tools and platforms to drive results' },
  { id:'sk035', name:'Fundraising',                          category:'Role-Specific Technical/Functional', description:'Securing financial support for programs and initiatives' },
  { id:'sk036', name:'Event Planning',                       category:'Role-Specific Technical/Functional', description:'Organizing and executing successful events' },
  { id:'sk037', name:'Product Development',                  category:'Role-Specific Technical/Functional', description:'Designing and launching new products or services' },
  { id:'sk038', name:'Marketing',                            category:'Role-Specific Technical/Functional', description:'Promoting programs, products, and the organization\'s mission' },
  { id:'sk039', name:'Community Advocacy',                   category:'Role-Specific Technical/Functional', description:'Representing and championing community needs and interests' },
  { id:'sk040', name:'Legal',                                category:'Role-Specific Technical/Functional', description:'Understanding relevant laws, regulations, and compliance requirements' },
  { id:'sk041', name:'Accounting',                           category:'Role-Specific Technical/Functional', description:'Managing financial records, budgets, and reporting' },
  // -- LEADING TEAMS - Leadership Soft Skills------
  { id:'sk042', name:'Able to Have Difficult Conversations',          category:'Leadership Soft Skills', description:'Addressing sensitive issues directly and constructively' },
  { id:'sk043', name:'Align Team Goals and Priorities',               category:'Leadership Soft Skills', description:'Ensuring team objectives connect to organizational strategy' },
  { id:'sk044', name:'Collaborative Leadership',                      category:'Leadership Soft Skills', description:'Leading through partnership, inclusion, and shared ownership' },
  { id:'sk045', name:'Conflict Management',                           category:'Leadership Soft Skills', description:'Resolving disagreements fairly and productively' },
  { id:'sk046', name:'Empowering Others',                             category:'Leadership Soft Skills', description:'Giving team members autonomy and confidence to succeed' },
  { id:'sk047', name:'Foster Creativity',                             category:'Leadership Soft Skills', description:'Creating an environment where new ideas are encouraged' },
  { id:'sk048', name:'Gives Effective Feedback',                      category:'Leadership Soft Skills', description:'Delivering timely, specific, and actionable feedback' },
  { id:'sk049', name:'Manage Remote Teams',                           category:'Leadership Soft Skills', description:'Leading and engaging distributed or hybrid teams effectively' },
  { id:'sk050', name:'Mentoring & Coaching',                          category:'Leadership Soft Skills', description:'Supporting team members\' growth through guidance and development' },
  { id:'sk051', name:'Motivates Through Rewards and Recognition',     category:'Leadership Soft Skills', description:'Acknowledging and celebrating contributions to drive engagement' },
  { id:'sk052', name:'Set Expectations & Hold People Accountable',    category:'Leadership Soft Skills', description:'Establishing clear standards and following through on accountability' },
  { id:'sk053', name:'Situational Leadership',                        category:'Leadership Soft Skills', description:'Adapting leadership style to match team member needs and readiness' },
  // -- LEADING TEAMS - Managing People------------------------------------------------
  { id:'sk054', name:'Assign Roles and Responsibilities',             category:'Managing People', description:'Clearly defining who is responsible for what on the team' },
  { id:'sk055', name:'Conducts Effective Meetings',                   category:'Managing People', description:'Running focused, productive meetings with clear outcomes' },
  { id:'sk056', name:'Delegation',                                    category:'Managing People', description:'Entrusting tasks and authority to the right team members' },
  { id:'sk057', name:'Demonstrate Support & Allocate Resources',      category:'Managing People', description:'Providing the tools, time, and backing team members need' },
  { id:'sk058', name:'Ensure Staff Adhere to Policies and Procedures',category:'Managing People', description:'Maintaining compliance with organizational standards' },
  { id:'sk059', name:'Manage Performance',                            category:'Managing People', description:'Setting goals, monitoring progress, and addressing performance issues' },
  // -- LEADING DEPARTMENTS - Managing Teams------------------
  { id:'sk060', name:'Builds Consensus',                    category:'Managing Teams',        description:'Gaining alignment and buy-in across diverse stakeholders' },
  { id:'sk061', name:'Develop Others',                      category:'Managing Teams',        description:'Investing in the long-term growth and capability of team members' },
  { id:'sk062', name:'Interviewing',                        category:'Managing Teams',        description:'Effectively selecting talent through structured interview processes' },
  { id:'sk063', name:'Workforce Management',                category:'Managing Teams',        description:'Planning and optimizing staffing levels and team structure' },
  // -- LEADING DEPARTMENTS - Department Leadership ------------------------------------------------------------------------------------------------------------------------------------------------------------------
  { id:'sk064', name:'Builds a Culture of Inclusion and Belonging', category:'Department Leadership', description:'Creating an environment where everyone feels valued and respected' },
  { id:'sk065', name:'Drives Strategic Direction',                  category:'Department Leadership', description:'Setting and pursuing a clear vision for the department' },
  { id:'sk066', name:'Inspires Others to Action',                   category:'Department Leadership', description:'Motivating people to commit to and execute on shared goals' },
  { id:'sk067', name:'Sets Department Goals',                       category:'Department Leadership', description:'Establishing measurable objectives aligned with organizational priorities' },
  // -- LEADING ORGANIZATIONS - Leading Division ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  { id:'sk068', name:'Accountability to Stakeholders',              category:'Leading Division', description:'Reporting transparently and delivering on commitments to key stakeholders' },
  { id:'sk069', name:'Board Management',                            category:'Leading Division', description:'Effectively engaging and working with boards of directors' },
  { id:'sk070', name:'Ensure Organizational Alignment',            category:'Leading Division', description:'Keeping all parts of the organization moving in the same direction' },
  { id:'sk071', name:'Ensure Relevance, Agility and Sustainability',category:'Leading Division', description:'Positioning the organization to thrive long-term in a changing environment' },
  { id:'sk072', name:'Establish Strategic Priorities',             category:'Leading Division', description:'Identifying and focusing resources on what matters most' },
  { id:'sk073', name:'Execution',                                   category:'Leading Division', description:'Translating strategy into disciplined, results-oriented action' },
  { id:'sk074', name:'Organizational Awareness',                   category:'Leading Division', description:'Understanding the informal dynamics and culture of the organization' },
  { id:'sk075', name:'Set and Communicate Vision',                 category:'Leading Division', description:'Articulating a compelling direction that motivates the whole organization' },
  { id:'sk076', name:'Set the Culture',                            category:'Leading Division', description:'Modeling and shaping the values and norms of the organization' },
  // -- LEADING ORGANIZATIONS - Business Results ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  { id:'sk077', name:'Change Management',                          category:'Business Results', description:'Leading people and processes through organizational transitions' },
  { id:'sk078', name:'Competitive Intelligence',                   category:'Business Results', description:'Gathering and applying knowledge about the external environment' },
  { id:'sk079', name:'Continuous Process Improvement',             category:'Business Results', description:'Identifying and eliminating inefficiencies on an ongoing basis' },
  { id:'sk080', name:'Creative Problem Solving',                   category:'Business Results', description:'Generating innovative solutions to complex challenges' },
  { id:'sk081', name:'Financial Management',                       category:'Business Results', description:'Overseeing budgets, forecasts, and financial health of the organization' },
  { id:'sk082', name:'Innovation',                                 category:'Business Results', description:'Driving new ideas, products, and approaches that create value' },
  { id:'sk083', name:'Measure Impact / Use Data to Drive Decisions',category:'Business Results', description:'Using metrics and evidence to evaluate effectiveness and guide strategy' },
  { id:'sk084', name:'Negotiation',                                category:'Business Results', description:'Reaching mutually beneficial agreements with internal and external parties' },
  { id:'sk085', name:'Quality Management',                         category:'Business Results', description:'Maintaining and improving the standards of organizational output' },
  { id:'sk086', name:'Risk Management',                            category:'Business Results', description:'Identifying, assessing, and mitigating organizational risks' },
  // -- LEADING ORGANIZATIONS - Client Community ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  { id:'sk087', name:'Build Customer Relationships',               category:'Client Community', description:'Developing lasting trust and rapport with the communities served' },
  { id:'sk088', name:'Close the Loop',                             category:'Client Community', description:'Following up to ensure issues are fully resolved and communicated' },
  { id:'sk089', name:'Community Awareness',                        category:'Client Community', description:'Understanding the needs, values, and context of the community' },
  { id:'sk090', name:'Develops Strategic Partnerships',            category:'Client Community', description:'Building alliances that expand reach and impact' },
  { id:'sk091', name:'Focuses on Customer (Community)',            category:'Client Community', description:'Centering all decisions around the needs of the community served' },
  { id:'sk092', name:'Leverage Networks',                          category:'Client Community', description:'Using relationships and connections to advance organizational goals' },
  { id:'sk093', name:'Manages Stakeholders',                       category:'Client Community', description:'Engaging and balancing the interests of diverse stakeholder groups' },
  { id:'sk094', name:'Political Savvy / Diplomacy',                category:'Client Community', description:'Navigating complex political and relational dynamics with skill' },
];

const AVATAR_PALETTE = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4'];
const REGIONS = ['North','South','East','West','Central','International','Remote'];
const TASK_CATS = ['Onboarding','Leadership','Compliance','Technical','Soft Skills','Role-Specific','Wellbeing','Other'];

// UTILITIES
const uid = () => `${Date.now()}${Math.random().toString(36).slice(2,7)}`;
const initials = name => (name||'?').split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '------';
const today = () => new Date().toISOString().split('T')[0];
const isOverdue = d => d && new Date(d+'T23:59:59') < new Date();

const avatarColor = name => {
  let h=0; for(const c of (name||'?')) h=c.charCodeAt(0)+((h<<5)-h);
  return AVATAR_PALETTE[Math.abs(h)%AVATAR_PALETTE.length];
};

const getProgress = (progress, staffId, taskId) => progress[staffId]?.[taskId] || { status:'not_started', completedDate:null, notes:'' };

const setProgress = (prev, staffId, taskId, val) => ({
  ...prev,
  [staffId]: { ...(prev[staffId]||{}), [taskId]: val }
});

// INITIAL / STORAGE
const DEFAULT_MASTER_CATS = [
  { id:'mc_ou', name:'OU Skills Inventory', color:'#1d4ed8', isBuiltIn:true },
];

const BLANK = () => ({
  staff: [],
  tasks: [],
  progress: {},
  skills: PRELOADED_SKILLS.map(s=>({...s, isCustom:false, masterCategory:'OU Skills Inventory'})),
  customCategories: [],   // [{name, color, masterCategoryId}]
  masterCategories: DEFAULT_MASTER_CATS,
});

function usePlatformData() {
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem('stafffirst_v2');
      if (raw) {
        const parsed = JSON.parse(raw);
        const existIds = new Set(parsed.skills.map(s=>s.id));
        const missing = PRELOADED_SKILLS.filter(s=>!existIds.has(s.id))
          .map(s=>({...s, isCustom:false, masterCategory:'OU Skills Inventory'}));
        const updatedSkills = parsed.skills.map(s=>
          (!s.isCustom && !s.masterCategory) ? {...s, masterCategory:'OU Skills Inventory'} : s
        );
        const existMasterIds = new Set((parsed.masterCategories||[]).map(m=>m.id));
        const missingMasters = DEFAULT_MASTER_CATS.filter(m=>!existMasterIds.has(m.id));
        return {
          ...BLANK(), ...parsed,
          skills: [...updatedSkills, ...missing],
          masterCategories: [...(parsed.masterCategories||[]), ...missingMasters],
        };
      }
    } catch(e){}
    return BLANK();
  });

  useEffect(() => {
    try { localStorage.setItem('stafffirst_v2', JSON.stringify(data)); } catch(e){}
  }, [data]);

  const update = fn => setData(prev => ({ ...prev, ...fn(prev) }));
  return [data, update];
}


// GLOBAL CSS
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
  body { font-family: 'DM Sans', sans-serif; background: #f1f5f9; color: #1e293b; }
  h1,h2,h3,h4,h5 { font-family: 'Sora', sans-serif; }
  ::-webkit-scrollbar { width:6px; height:6px; }
  ::-webkit-scrollbar-track { background:#f1f5f9; }
  ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }
  input:focus, select:focus, textarea:focus { outline:none; border-color:#6366f1 !important; box-shadow:0 0 0 3px rgba(99,102,241,0.12) !important; }
  .row-hover:hover { background:#fafbff !important; }
  .btn-anim { transition: opacity 0.15s, transform 0.1s; }
  .btn-anim:hover:not(:disabled) { opacity:0.88; }
  .btn-anim:active:not(:disabled) { transform:scale(0.97); }
  .card { background:#fff; border-radius:12px; border:1px solid #e2e8f0; }
  .fade-in { animation: fadeIn 0.2s ease; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .tab-active { color:#6366f1 !important; border-bottom:2px solid #6366f1 !important; }
  .skill-chip:hover { transform:translateY(-1px); box-shadow:0 4px 12px rgba(0,0,0,0.1); }
`;

// SHARED COMPONENTS
function Avatar({ name, size=36 }) {
  const color = avatarColor(name);
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:color,
      display:'flex',alignItems:'center',justifyContent:'center',
      color:'#fff', fontWeight:700, fontSize:size*0.38, flexShrink:0, fontFamily:'Sora,sans-serif',
      border:'2px solid rgba(255,255,255,0.8)' }}>
      {initials(name)}
    </div>
  );
}

function Badge({ label, color='#6366f1', sm=true }) {
  return (
    <span style={{ background:`${color}18`, color, border:`1px solid ${color}35`,
      borderRadius:20, padding:sm?'2px 9px':'4px 12px', fontSize:sm?11:13, fontWeight:600, whiteSpace:'nowrap' }}>
      {label}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = { not_started:{l:'Not Started',c:'#94a3b8'}, in_progress:{l:'In Progress',c:'#f59e0b'}, completed:{l:'Completed',c:'#10b981'} };
  const s = map[status]||map.not_started;
  return <Badge label={s.l} color={s.c} sm />;
}

function Btn({ children, variant='primary', onClick, style={}, disabled=false, size='md', type='button' }) {
  const v = { primary:{bg:'#6366f1',c:'#fff',b:'none'}, secondary:{bg:'#f1f5f9',c:'#475569',b:'1px solid #e2e8f0'},
    danger:{bg:'#fef2f2',c:'#ef4444',b:'1px solid #fee2e2'}, ghost:{bg:'transparent',c:'#6366f1',b:'1px solid #c7d2fe'},
    success:{bg:'#f0fdf4',c:'#10b981',b:'1px solid #bbf7d0'} };
  const sz = { sm:{p:'5px 12px',fs:12}, md:{p:'9px 18px',fs:14}, lg:{p:'12px 28px',fs:15} };
  const vs=v[variant]||v.primary; const ss=sz[size]||sz.md;
  return (
    <button type={type} onClick={onClick} disabled={disabled} className='btn-anim'
      style={{ background:vs.bg,color:vs.c,border:vs.b, padding:ss.p,fontSize:ss.fs,
        borderRadius:8, fontWeight:600, cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.55:1,
        display:'inline-flex',alignItems:'center',gap:6, fontFamily:'inherit', ...style }}>
      {children}
    </button>
  );
}

function FInput({ label, required, ...props }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:5 }}>{label}{required&&<span style={{color:'#ef4444'}}> *</span>}</label>}
      <input {...props} style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,
        padding:'9px 12px',fontSize:14,color:'#1e293b',background:'#f8fafc', ...props.style }} />
    </div>
  );
}

function FSelect({ label, required, children, ...props }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:5 }}>{label}{required&&<span style={{color:'#ef4444'}}> *</span>}</label>}
      <select {...props} style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,
        padding:'9px 12px',fontSize:14,color:'#1e293b',background:'#f8fafc',cursor:'pointer', ...props.style }}>
        {children}
      </select>
    </div>
  );
}

function FTextarea({ label, ...props }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:5 }}>{label}</label>}
      <textarea {...props} style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,
        padding:'9px 12px',fontSize:14,color:'#1e293b',background:'#f8fafc',resize:'vertical',minHeight:80, ...props.style }} />
    </div>
  );
}

function Modal({ title, onClose, children, width=580 }) {
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.65)',zIndex:1000,
      display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:width,
        maxHeight:'90vh',overflow:'auto',boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }} className='fade-in'>
        <div style={{ padding:'20px 24px',borderBottom:'1px solid #e2e8f0',
          display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1 }}>
          <h3 style={{ margin:0,fontSize:17,fontWeight:700,color:'#1e293b',fontFamily:'Sora,sans-serif' }}>{title}</h3>
          <button onClick={onClose} style={{ background:'#f1f5f9',border:'none',cursor:'pointer',
            color:'#64748b',width:32,height:32,borderRadius:8,fontSize:18,display:'flex',alignItems:'center',justifyContent:'center' }}>----</button>
        </div>
        <div style={{ padding:24 }}>{children}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, desc, action }) {
  return (
    <div style={{ textAlign:'center',padding:'60px 20px' }}>
      <div style={{ fontSize:52,marginBottom:16 }}>{icon}</div>
      <div style={{ fontSize:17,fontWeight:700,color:'#1e293b',marginBottom:8,fontFamily:'Sora,sans-serif' }}>{title}</div>
      <div style={{ color:'#64748b',marginBottom:24,fontSize:14,maxWidth:360,margin:'0 auto 24px' }}>{desc}</div>
      {action}
    </div>
  );
}

// SIDEBAR
const NAV = [
  { id:'dashboard', label:'Dashboard',    emoji:'[#]' },
  { id:'staff',     label:'Staff',        emoji:'[P]' },
  { id:'tasks',     label:'PD Tasks',     emoji:'[T]' },
  { id:'skills',    label:'Skills Library',emoji:'[S]' },
];

function Sidebar({ view, nav }) {
  const active = (id) => view===id || (id==='staff'&&view==='staff-profile') || (id==='tasks'&&view==='task-detail');
  return (
    <div style={{ width:220,background:'#0f172a',height:'100vh',position:'fixed',left:0,top:0,
      display:'flex',flexDirection:'column',zIndex:200 }}>
      {/* Logo */}
      <div style={{ padding:'16px 20px 14px',borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
          <img src='/ncsy-logo.webp' alt='NCSY' style={{ width:44,height:44,objectFit:'contain',borderRadius:6,background:'#fff',padding:3 }} />
          <div>
            <div style={{ color:'#e2e8f0',fontWeight:800,fontSize:15,fontFamily:'Sora,sans-serif',letterSpacing:'-0.02em' }}>StaffFirst</div>
            <div style={{ color:'#475569',fontSize:10,letterSpacing:'0.08em',textTransform:'uppercase' }}>Management Platform</div>
          </div>
        </div>
      </div>
      {/* Nav */}
      <nav style={{ flex:1,padding:'16px 10px',overflowY:'auto' }}>
        <div style={{ fontSize:10,fontWeight:700,color:'#334155',letterSpacing:'0.1em',textTransform:'uppercase',
          padding:'0 10px',marginBottom:8 }}>NAVIGATION</div>
        {NAV.map(item => {
          const on = active(item.id);
          return (
            <button key={item.id} onClick={()=>nav(item.id)} className='btn-anim'
              style={{ display:'flex',alignItems:'center',gap:10,width:'100%',padding:'10px 12px',
                borderRadius:8,marginBottom:2,background:on?'rgba(99,102,241,0.18)':'transparent',
                border:on?'1px solid rgba(99,102,241,0.25)':'1px solid transparent',
                cursor:'pointer',color:on?'#818cf8':'#64748b',fontSize:14,fontWeight:on?600:400,
                textAlign:'left',fontFamily:'inherit' }}>
              <span style={{ fontSize:16,width:20,textAlign:'center' }}>{item.emoji}</span>
              {item.label}
              {on && <div style={{ marginLeft:'auto',width:6,height:6,borderRadius:'50%',background:'#818cf8' }}/>}
            </button>
          );
        })}
      </nav>
      <div style={{ padding:'14px 20px',borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ color:'#334155',fontSize:11 }}>StaffFirst - v1.0</div>
      </div>
    </div>
  );
}

// DASHBOARD
function Dashboard({ data, nav }) {
  const { staff, tasks, progress, skills } = data;

  let completedCount=0, totalAssignments=0;
  staff.forEach(s => tasks.forEach(t => {
    if(t.assignedStaffIds.includes(s.id)) {
      totalAssignments++;
      const p = getProgress(progress, s.id, t.id);
      if(p.status==='completed') completedCount++;
    }
  }));

  const overdueTaskIds = new Set(tasks.filter(t => {
    if(!isOverdue(t.dueDate)) return false;
    return t.assignedStaffIds.some(sid => getProgress(progress,sid,t.id).status!=='completed');
  }).map(t=>t.id));

  // Recent completions
  const recentCompletions = [];
  staff.forEach(s => tasks.forEach(t => {
    if(!t.assignedStaffIds.includes(s.id)) return;
    const p = getProgress(progress,s.id,t.id);
    if(p.status==='completed' && p.completedDate)
      recentCompletions.push({ staffName:s.name, taskTitle:t.title, completedDate:p.completedDate });
  }));
  recentCompletions.sort((a,b)=>new Date(b.completedDate)-new Date(a.completedDate));

  // Upcoming tasks (not overdue, sorted by due date)
  const upcoming = tasks
    .filter(t=>!overdueTaskIds.has(t.id) && t.dueDate)
    .sort((a,b)=>new Date(a.dueDate)-new Date(b.dueDate))
    .slice(0,4);

  const overdueTasks = tasks.filter(t=>overdueTaskIds.has(t.id)).slice(0,4);

  const stats = [
    { label:'Total Staff',        val:staff.length,        icon:'Staff', color:'#6366f1', bg:'#eef2ff' },
    { label:'Active Tasks',       val:tasks.length,        icon:'Tasks', color:'#0ea5e9', bg:'#f0f9ff' },
    { label:'Completions',        val:completedCount,      icon:'Done', color:'#10b981', bg:'#f0fdf4' },
    { label:'Overdue',            val:overdueTaskIds.size, icon:'Late', color:'#ef4444', bg:'#fef2f2' },
  ];

  return (
    <div className='fade-in'>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:26,fontWeight:800,color:'#1e293b',margin:0 }}>Dashboard</h1>
        <p style={{ color:'#64748b',marginTop:4,fontSize:14 }}>
          {new Date().toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
        </p>
      </div>

      {/* Stat Cards */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24 }}>
        {stats.map(s=>(
          <div key={s.label} className='card' style={{ padding:20,display:'flex',alignItems:'center',gap:14 }}>
            <div style={{ width:50,height:50,borderRadius:12,background:s.bg,
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,flexShrink:0 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize:30,fontWeight:800,color:s.color,lineHeight:1,fontFamily:'Sora,sans-serif' }}>{s.val}</div>
              <div style={{ fontSize:13,color:'#64748b',marginTop:3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20 }}>
        {/* Recent Completions */}
        <div className='card' style={{ padding:20 }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
            <h3 style={{ fontSize:15,fontWeight:700,color:'#1e293b' }}>Recent Completions</h3>
            <span style={{ fontSize:12,color:'#94a3b8' }}>Latest first</span>
          </div>
          {recentCompletions.length===0 ? (
            <div style={{ color:'#94a3b8',fontSize:14,textAlign:'center',padding:'28px 0' }}>No completions yet</div>
          ) : recentCompletions.slice(0,6).map((c,i)=>(
            <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 0',
              borderBottom:i<Math.min(recentCompletions.length,6)-1?'1px solid #f1f5f9':'none' }}>
              <Avatar name={c.staffName} size={30} />
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:13,fontWeight:600,color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{c.staffName}</div>
                <div style={{ fontSize:12,color:'#64748b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{c.taskTitle}</div>
              </div>
              <div style={{ fontSize:11,color:'#10b981',fontWeight:600,whiteSpace:'nowrap' }}>{fmtDate(c.completedDate)}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className='card' style={{ padding:20 }}>
          <h3 style={{ fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:14 }}>Quick Actions</h3>
          {[
            { label:'Add Staff Member',    desc:'Register a new employee',         icon:'--', action:()=>nav('staff') },
            { label:'Create PD Task',      desc:'Assign professional development', icon:'*', action:()=>nav('tasks') },
            { label:'Browse Skills Library',desc:'View or add custom skills',      icon:'----', action:()=>nav('skills') },
            { label:'View All Staff',      desc:'See progress overview',           icon:'*', action:()=>nav('staff') },
          ].map((qa,i)=>(
            <button key={i} onClick={qa.action} className='btn-anim'
              style={{ display:'flex',alignItems:'center',gap:12,width:'100%',padding:'10px 12px',
                background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:8,cursor:'pointer',
                marginBottom:8,textAlign:'left',fontFamily:'inherit',transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#eef2ff'}
              onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}>
              <span style={{ fontSize:20,width:28,textAlign:'center' }}>{qa.icon}</span>
              <div>
                <div style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{qa.label}</div>
                <div style={{ fontSize:11,color:'#64748b' }}>{qa.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Overdue Tasks */}
        {overdueTasks.length>0 && (
          <div className='card' style={{ padding:20 }}>
            <h3 style={{ fontSize:15,fontWeight:700,color:'#ef4444',marginBottom:14 }}>-- Overdue Tasks</h3>
            {overdueTasks.map(t=>{
              const pending = t.assignedStaffIds.filter(sid=>getProgress(progress,sid,t.id).status!=='completed').length;
              return (
                <div key={t.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'9px 0',
                  borderBottom:'1px solid #fef2f2' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{t.title}</div>
                    <div style={{ fontSize:12,color:'#94a3b8' }}>{pending} staff pending - Due {fmtDate(t.dueDate)}</div>
                  </div>
                  <Btn size='sm' variant='danger' onClick={()=>nav('task-detail',t.id)}>View</Btn>
                </div>
              );
            })}
          </div>
        )}

        {/* Upcoming Tasks */}
        {upcoming.length>0 && (
          <div className='card' style={{ padding:20 }}>
            <h3 style={{ fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:14 }}>Upcoming Due Dates</h3>
            {upcoming.map(t=>{
              const done = t.assignedStaffIds.filter(sid=>getProgress(progress,sid,t.id).status==='completed').length;
              return (
                <div key={t.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'9px 0',
                  borderBottom:'1px solid #f1f5f9' }}>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:'#f59e0b',flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{t.title}</div>
                    <div style={{ fontSize:12,color:'#94a3b8' }}>{done}/{t.assignedStaffIds.length} done - Due {fmtDate(t.dueDate)}</div>
                  </div>
                  <Btn size='sm' variant='secondary' onClick={()=>nav('task-detail',t.id)}>View</Btn>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ADD STAFF MODAL
function AddStaffModal({ data, update, onClose, editingStaff=null }) {
  const [form, setForm] = useState(editingStaff || {
    name:'', position:'', startDate:'', region:'', email:'', phone:'', managerId:'', role:'staff'
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = () => {
    if(!form.name.trim()||!form.position.trim()) return alert('Name and position are required.');
    if(editingStaff) {
      update(prev=>({ staff:prev.staff.map(s=>s.id===editingStaff.id?{...s,...form}:s) }));
    } else {
      update(prev=>({ staff:[...prev.staff,{...form,id:uid()}] }));
    }
    onClose();
  };

  const managers = data.staff.filter(s=>s.id!==editingStaff?.id);

  return (
    <Modal title={editingStaff?'Edit Staff Member':'Add Staff Member'} onClose={onClose}>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px' }}>
        <div style={{ gridColumn:'1/-1' }}>
          <FInput label='Full Name' required value={form.name} onChange={e=>set('name',e.target.value)} placeholder='Jane Smith' />
        </div>
        <FInput label='Position / Job Title' required value={form.position} onChange={e=>set('position',e.target.value)} placeholder='Customer Success Manager' />
        <FSelect label='Region' value={form.region} onChange={e=>set('region',e.target.value)}>
          <option value=''>Select region----</option>
          {REGIONS.map(r=><option key={r} value={r}>{r}</option>)}
        </FSelect>
        <FInput label='Start Date' type='date' value={form.startDate} onChange={e=>set('startDate',e.target.value)} />
        <FSelect label='Role' value={form.role} onChange={e=>set('role',e.target.value)}>
          <option value='staff'>Staff</option>
          <option value='manager'>Manager</option>
          <option value='admin'>Admin</option>
        </FSelect>
        <FInput label='Email' type='email' value={form.email} onChange={e=>set('email',e.target.value)} placeholder='jane@company.com' />
        <FInput label='Phone' type='tel' value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder='+1 555 000 0000' />
        <FSelect label='Manager' value={form.managerId||''} onChange={e=>set('managerId',e.target.value)}>
          <option value=''>No manager assigned</option>
          {managers.map(m=><option key={m.id} value={m.id}>{m.name} - {m.position}</option>)}
        </FSelect>
      </div>
      <div style={{ display:'flex',justifyContent:'flex-end',gap:10,marginTop:8,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
        <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
        <Btn onClick={save}>{editingStaff?'Save Changes':'Add Staff Member'}</Btn>
      </div>
    </Modal>
  );
}

// BULK UPLOAD MODAL
function BulkUploadModal({ data, update, onClose }) {
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const fileRef = useRef();

  const REQUIRED = ['name','position'];
  const COLS = ['name','position','start_date','region','email','phone','manager_email','role'];

  const downloadTemplate = () => {
    const csv = [COLS.join(','),
      'Jane Smith,Operations Manager,2024-01-15,North,jane@company.com,+1-555-0101,,manager',
      'John Doe,Customer Support,2024-03-01,South,john@company.com,+1-555-0102,jane@company.com,staff',
    ].join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv,' + encodeURIComponent(csv);
    a.download = 'stafffirst_staff_template.csv';
    a.click();
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if(!file) return;
    Papa.parse(file, {
      header:true, skipEmptyLines:true,
      complete: result => {
        const errs=[];
        result.data.forEach((row,i)=>{
          REQUIRED.forEach(f=>{ if(!row[f]&&!row[f.charAt(0).toUpperCase()+f.slice(1)]) errs.push(`Row ${i+2}: missing "${f}"`); });
        });
        setErrors(errs);
        setPreview(result.data.slice(0,5));
        setImporting(false);
      }
    });
  };

  const doImport = () => {
    const file = fileRef.current.files[0];
    if(!file) return;
    setImporting(true);
    Papa.parse(file, {
      header:true, skipEmptyLines:true,
      complete: result => {
        const newStaff = result.data.map(row=>{
          const get = (...keys) => { for(const k of keys) if(row[k]) return row[k]; return ''; };
          const managerEmail = get('manager_email','Manager Email','Manager_Email');
          const existingManager = data.staff.find(s=>s.email===managerEmail)||null;
          return {
            id:uid(),
            name:get('name','Name','full_name'),
            position:get('position','Position','job_title','Job Title'),
            startDate:get('start_date','Start Date','startDate'),
            region:get('region','Region'),
            email:get('email','Email'),
            phone:get('phone','Phone'),
            managerId:existingManager?existingManager.id:'',
            role:get('role','Role')||'staff',
          };
        }).filter(s=>s.name.trim());
        update(prev=>({ staff:[...prev.staff,...newStaff] }));
        setImported(true);
        setImporting(false);
      }
    });
  };

  return (
    <Modal title='Bulk Upload Staff' onClose={onClose} width={640}>
      {imported ? (
        <div style={{ textAlign:'center',padding:'32px 0' }}>
          <div style={{ fontSize:52,marginBottom:16 }}>----</div>
          <h3 style={{ fontFamily:'Sora,sans-serif',color:'#1e293b' }}>Import Successful!</h3>
          <p style={{ color:'#64748b',marginTop:8,marginBottom:24 }}>Staff members have been added to the platform.</p>
          <Btn onClick={onClose}>Done</Btn>
        </div>
      ) : (
        <>
          {/* Instructions */}
          <div style={{ background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'12px 16px',marginBottom:20 }}>
            <div style={{ fontSize:13,fontWeight:600,color:'#0369a1',marginBottom:6 }}> CSV Format</div>
            <div style={{ fontSize:12,color:'#0369a1',fontFamily:'monospace' }}>{COLS.join(', ')}</div>
            <div style={{ fontSize:12,color:'#0369a1',marginTop:6 }}>Required: <strong>name, position</strong> - All other fields optional</div>
          </div>
          <div style={{ display:'flex',gap:10,marginBottom:20 }}>
            <Btn variant='ghost' onClick={downloadTemplate} size='sm'>---- Download Template CSV</Btn>
          </div>

          {/* File Drop Zone */}
          <div style={{ border:'2px dashed #c7d2fe',borderRadius:10,padding:'32px 20px',
            textAlign:'center',cursor:'pointer',background:'#fafbff',marginBottom:16 }}
            onClick={()=>fileRef.current.click()}>
            <div style={{ fontSize:32,marginBottom:8 }}>--</div>
            <div style={{ fontSize:14,fontWeight:600,color:'#6366f1' }}>Click to select CSV file</div>
            <div style={{ fontSize:12,color:'#94a3b8',marginTop:4 }}>or drag and drop</div>
            <input ref={fileRef} type='file' accept='.csv' style={{ display:'none' }} onChange={handleFile} />
          </div>

          {/* Errors */}
          {errors.length>0 && (
            <div style={{ background:'#fef2f2',border:'1px solid #fee2e2',borderRadius:8,padding:12,marginBottom:16 }}>
              <div style={{ fontSize:13,fontWeight:600,color:'#ef4444',marginBottom:6 }}>Validation Warnings</div>
              {errors.slice(0,5).map((e,i)=><div key={i} style={{ fontSize:12,color:'#ef4444' }}>{e}</div>)}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>Preview (first 5 rows)</div>
              <div style={{ overflowX:'auto',border:'1px solid #e2e8f0',borderRadius:8 }}>
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      {Object.keys(preview[0]).map(k=>(
                        <th key={k} style={{ padding:'8px 10px',textAlign:'left',color:'#64748b',fontWeight:600,borderBottom:'1px solid #e2e8f0' }}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row,i)=>(
                      <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                        {Object.values(row).map((v,j)=>(<td key={j} style={{ padding:'7px 10px',color:'#1e293b' }}>{v||'------'}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
            <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
            <Btn onClick={doImport} disabled={!preview||importing}>{importing?'Importing----':'Import Staff'}</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// STAFF LIST
function StaffList({ data, update, nav }) {
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);

  const regions = [...new Set(data.staff.map(s=>s.region).filter(Boolean))];

  const filtered = data.staff.filter(s=>{
    const q=search.toLowerCase();
    return (!q||s.name.toLowerCase().includes(q)||s.position.toLowerCase().includes(q)||(s.email||'').toLowerCase().includes(q))
      && (region==='all'||s.region===region);
  });

  const del = id => { if(window.confirm('Remove this staff member and all their task records?'))
    update(prev=>({ staff:prev.staff.filter(s=>s.id!==id), progress:Object.fromEntries(Object.entries(prev.progress).filter(([k])=>k!==id)) }));
  };

  const getTaskStats = (s) => {
    const assigned = data.tasks.filter(t=>t.assignedStaffIds.includes(s.id));
    const done = assigned.filter(t=>getProgress(data.progress,s.id,t.id).status==='completed');
    return { total:assigned.length, done:done.length };
  };

  return (
    <div className='fade-in'>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:26,fontWeight:800,color:'#1e293b',margin:0 }}>Staff</h1>
          <p style={{ color:'#64748b',marginTop:4,fontSize:14 }}>{data.staff.length} member{data.staff.length!==1?'s':''} registered</p>
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <Btn variant='secondary' onClick={()=>setShowBulk(true)}>-- Bulk Upload</Btn>
          <Btn onClick={()=>setShowAdd(true)}>+ Add Staff</Btn>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex',gap:12,marginBottom:20,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:240 }}>
          <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#94a3b8' }}>--</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder='Search name, position, email...'
            style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'9px 12px 9px 36px',
              fontSize:14,background:'#fff',color:'#1e293b' }} />
        </div>
        <select value={region} onChange={e=>setRegion(e.target.value)}
          style={{ border:'1px solid #e2e8f0',borderRadius:8,padding:'9px 14px',fontSize:14,background:'#fff',cursor:'pointer',color:'#1e293b' }}>
          <option value='all'>All Regions</option>
          {regions.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {filtered.length===0 ? (
        <div className='card'>
          <EmptyState icon='' title={data.staff.length===0?'No staff yet':'No results'}
            desc={data.staff.length===0?'Add your first staff member manually or import a CSV file':'Try adjusting your search or filters'}
            action={data.staff.length===0&&(
              <div style={{ display:'flex',gap:10,justifyContent:'center' }}>
                <Btn variant='secondary' onClick={()=>setShowBulk(true)}>-- Bulk Upload CSV</Btn>
                <Btn onClick={()=>setShowAdd(true)}>+ Add Staff Member</Btn>
              </div>
            )} />
        </div>
      ) : (
        <div className='card' style={{ overflow:'hidden' }}>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc',borderBottom:'1px solid #e2e8f0' }}>
                {['Staff Member','Position','Region','Start Date','Contact','PD Progress',''].map(h=>(
                  <th key={h} style={{ padding:'11px 16px',textAlign:'left',fontSize:11,
                    fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s,i)=>{
                const stats = getTaskStats(s);
                const mgr = data.staff.find(x=>x.id===s.managerId);
                const pct = stats.total>0?Math.round((stats.done/stats.total)*100):0;
                return (
                  <tr key={s.id} className='row-hover' style={{ borderBottom:i<filtered.length-1?'1px solid #f1f5f9':'none',cursor:'pointer' }}
                    onClick={()=>nav('staff-profile',s.id)}>
                    <td style={{ padding:'13px 16px' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <Avatar name={s.name} size={36} />
                        <div>
                          <div style={{ fontWeight:600,color:'#1e293b',fontSize:14 }}>{s.name}</div>
                          {mgr&&<div style={{ fontSize:11,color:'#94a3b8' }}>-- {mgr.name}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'13px 16px',fontSize:13,color:'#475569' }}>{s.position}</td>
                    <td style={{ padding:'13px 16px' }}>{s.region?<Badge label={s.region} color='#6366f1' />:'------'}</td>
                    <td style={{ padding:'13px 16px',fontSize:12,color:'#64748b' }}>{fmtDate(s.startDate)}</td>
                    <td style={{ padding:'13px 16px' }}>
                      <div style={{ fontSize:12,color:'#475569' }}>{s.email||'------'}</div>
                      <div style={{ fontSize:11,color:'#94a3b8' }}>{s.phone||''}</div>
                    </td>
                    <td style={{ padding:'13px 16px' }}>
                      {stats.total>0 ? (
                        <div>
                          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4 }}>
                            <span style={{ fontSize:12,fontWeight:600,color:'#1e293b' }}>{stats.done}/{stats.total}</span>
                            <span style={{ fontSize:11,color:'#64748b' }}>{pct}%</span>
                          </div>
                          <div style={{ height:5,background:'#f1f5f9',borderRadius:3,overflow:'hidden' }}>
                            <div style={{ height:'100%',width:`${pct}%`,background:pct===100?'#10b981':'#6366f1',borderRadius:3,transition:'width 0.3s' }} />
                          </div>
                        </div>
                      ) : <span style={{ fontSize:12,color:'#94a3b8' }}>No tasks assigned</span>}
                    </td>
                    <td style={{ padding:'13px 16px' }} onClick={e=>e.stopPropagation()}>
                      <div style={{ display:'flex',gap:6 }}>
                        <Btn size='sm' variant='ghost' onClick={()=>nav('staff-profile',s.id)}>View</Btn>
                        <Btn size='sm' variant='secondary' onClick={()=>setEditing(s)}>Edit</Btn>
                        <Btn size='sm' variant='danger' onClick={()=>del(s.id)}>----</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddStaffModal data={data} update={update} onClose={()=>setShowAdd(false)} />}
      {showBulk && <BulkUploadModal data={data} update={update} onClose={()=>setShowBulk(false)} />}
      {editing && <AddStaffModal data={data} update={update} onClose={()=>setEditing(null)} editingStaff={editing} />}
    </div>
  );
}

// STAFF PROFILE
function StaffProfile({ staffId, data, update, nav }) {
  const [tab, setTab] = useState('tasks');
  const [showEdit, setShowEdit] = useState(false);
  const s = data.staff.find(x=>x.id===staffId);
  if(!s) return <div style={{ padding:40,color:'#64748b' }}>Staff member not found.</div>;

  const mgr = data.staff.find(x=>x.id===s.managerId);
  const assignedTasks = data.tasks.filter(t=>t.assignedStaffIds.includes(s.id));

  // Skills earned = skills from completed tasks
  const earnedSkillIds = new Set();
  assignedTasks.forEach(t=>{
    const p = getProgress(data.progress,s.id,t.id);
    if(p.status==='completed') t.skillIds.forEach(sid=>earnedSkillIds.add(sid));
  });
  const earnedSkills = data.skills.filter(sk=>earnedSkillIds.has(sk.id));

  const updateStatus = (taskId, status) => {
    const curr = getProgress(data.progress,s.id,taskId);
    const newP = { ...curr, status, completedDate: status==='completed'?today():null };
    update(prev=>({ progress:setProgress(prev.progress,s.id,taskId,newP) }));
  };

  const tabStyle = (t) => ({
    padding:'10px 18px', background:'transparent', border:'none', cursor:'pointer',
    fontSize:14, fontWeight:tab===t?700:500, color:tab===t?'#6366f1':'#64748b',
    borderBottom:tab===t?'2px solid #6366f1':'2px solid transparent',
    fontFamily:'inherit', transition:'color 0.15s',
  });

  const completedTasks = assignedTasks.filter(t=>getProgress(data.progress,s.id,t.id).status==='completed');
  const daysEmployed = s.startDate ? Math.floor((new Date()-new Date(s.startDate))/(1000*60*60*24)) : null;

  return (
    <div className='fade-in'>
      {/* Back */}
      <button onClick={()=>nav('staff')} className='btn-anim'
        style={{ background:'none',border:'none',cursor:'pointer',color:'#6366f1',fontSize:13,fontWeight:600,
          display:'flex',alignItems:'center',gap:6,marginBottom:20,fontFamily:'inherit' }}>
        ---- Back to Staff
      </button>

      {/* Header Card */}
      <div className='card' style={{ padding:24,marginBottom:20 }}>
        <div style={{ display:'flex',alignItems:'flex-start',gap:20 }}>
          <Avatar name={s.name} size={72} />
          <div style={{ flex:1 }}>
            <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:10 }}>
              <div>
                <h2 style={{ fontSize:22,fontWeight:800,color:'#1e293b',margin:0 }}>{s.name}</h2>
                <div style={{ fontSize:14,color:'#64748b',marginTop:4 }}>{s.position}</div>
                <div style={{ display:'flex',gap:8,marginTop:10,flexWrap:'wrap' }}>
                  {s.region&&<Badge label={` ${s.region}`} color='#6366f1' />}
                  <Badge label={s.role||'staff'} color='#0ea5e9' />
                  {daysEmployed!==null&&<Badge label={`${Math.floor(daysEmployed/365)}y ${Math.floor((daysEmployed%365)/30)}m tenure`} color='#10b981' />}
                </div>
              </div>
              <Btn variant='secondary' size='sm' onClick={()=>setShowEdit(true)}>---- Edit Profile</Btn>
            </div>
            <div style={{ display:'flex',gap:24,marginTop:14,flexWrap:'wrap' }}>
              {s.email&&<div style={{ fontSize:13,color:'#475569' }}>---- {s.email}</div>}
              {s.phone&&<div style={{ fontSize:13,color:'#475569' }}> {s.phone}</div>}
              {s.startDate&&<div style={{ fontSize:13,color:'#475569' }}> Started {fmtDate(s.startDate)}</div>}
              {mgr&&<div style={{ fontSize:13,color:'#475569' }}>-- Manager: {mgr.name}</div>}
            </div>
          </div>
        </div>
        {/* Mini Stats */}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginTop:20,
          padding:'16px 0 0',borderTop:'1px solid #f1f5f9' }}>
          {[
            { l:'Tasks Assigned', v:assignedTasks.length, c:'#6366f1' },
            { l:'Completed', v:completedTasks.length, c:'#10b981' },
            { l:'In Progress', v:assignedTasks.filter(t=>getProgress(data.progress,s.id,t.id).status==='in_progress').length, c:'#f59e0b' },
            { l:'Skills Earned', v:earnedSkills.length, c:'#8b5cf6' },
          ].map(stat=>(
            <div key={stat.l} style={{ textAlign:'center',padding:'10px',background:'#f8fafc',borderRadius:8 }}>
              <div style={{ fontSize:24,fontWeight:800,color:stat.c,fontFamily:'Sora,sans-serif' }}>{stat.v}</div>
              <div style={{ fontSize:11,color:'#64748b',marginTop:2 }}>{stat.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className='card' style={{ overflow:'hidden' }}>
        <div style={{ display:'flex',borderBottom:'1px solid #e2e8f0',padding:'0 8px' }}>
          <button style={tabStyle('tasks')} onClick={()=>setTab('tasks')}> PD Tasks ({assignedTasks.length})</button>
          <button style={tabStyle('skills')} onClick={()=>setTab('skills')}>---- Skills Earned ({earnedSkills.length})</button>
        </div>

        <div style={{ padding:20 }}>
          {tab==='tasks' && (
            assignedTasks.length===0 ? (
              <EmptyState icon='' title='No tasks assigned' desc='Assign PD tasks to this staff member from the PD Tasks section'
                action={<Btn onClick={()=>nav('tasks')}>Go to PD Tasks</Btn>} />
            ) : assignedTasks.map(t=>{
              const p = getProgress(data.progress,s.id,t.id);
              const od = isOverdue(t.dueDate) && p.status!=='completed';
              const taskSkills = data.skills.filter(sk=>t.skillIds.includes(sk.id));
              return (
                <div key={t.id} style={{ border:`1px solid ${od?'#fee2e2':'#e2e8f0'}`,borderRadius:10,
                  padding:16,marginBottom:12,background:od?'#fff5f5':'#fff' }}>
                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4 }}>
                        <span style={{ fontSize:14,fontWeight:700,color:'#1e293b' }}>{t.title}</span>
                        <StatusBadge status={p.status} />
                        {od&&<Badge label='-- Overdue' color='#ef4444' />}
                      </div>
                      {t.description&&<div style={{ fontSize:13,color:'#64748b',marginBottom:6 }}>{t.description}</div>}
                      <div style={{ fontSize:12,color:'#94a3b8' }}>Due: {fmtDate(t.dueDate)} {t.category&&`- ${t.category}`}</div>
                      {taskSkills.length>0&&(
                        <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginTop:8 }}>
                          {taskSkills.map(sk=>(
                            <span key={sk.id} style={{ fontSize:11,padding:'2px 8px',borderRadius:20,fontWeight:600,
                              background:`${getCatColors(data.customCategories)[sk.category]||'#6366f1'}15`,
                              color:getCatColors(data.customCategories)[sk.category]||'#6366f1',
                              border:`1px solid ${getCatColors(data.customCategories)[sk.category]||'#6366f1'}30` }}>
                              ---- {sk.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {p.completedDate&&<div style={{ fontSize:12,color:'#10b981',marginTop:6,fontWeight:600 }}>---- Completed {fmtDate(p.completedDate)}</div>}
                    </div>
                    <div style={{ display:'flex',gap:6,flexShrink:0 }}>
                      {p.status!=='completed'&&<Btn size='sm' variant='success' onClick={()=>updateStatus(t.id,'completed')}>---- Mark Done</Btn>}
                      {p.status==='not_started'&&<Btn size='sm' variant='secondary' onClick={()=>updateStatus(t.id,'in_progress')}>Start</Btn>}
                      {p.status==='completed'&&<Btn size='sm' variant='secondary' onClick={()=>updateStatus(t.id,'not_started')}>Reset</Btn>}
                      <Btn size='sm' variant='ghost' onClick={()=>nav('task-detail',t.id)}>Details</Btn>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {tab==='skills' && (
            earnedSkills.length===0 ? (
              <EmptyState icon='----' title='No skills earned yet'
                desc="Skills are automatically added to a staff member's profile when they complete a task that includes those skills" />
            ) : (
              <div>
                <div style={{ fontSize:13,color:'#64748b',marginBottom:16 }}>
                  Skills are earned by completing PD tasks with associated skills.
                </div>
                {Object.entries(
                  earnedSkills.reduce((acc,sk)=>({ ...acc, [sk.category]:[...(acc[sk.category]||[]),sk] }),{})
                ).map(([cat,skills])=>(
                  <div key={cat} style={{ marginBottom:20 }}>
                    <div style={{ fontSize:12,fontWeight:700,color:'#64748b',textTransform:'uppercase',
                      letterSpacing:'0.06em',marginBottom:10 }}>{cat}</div>
                    <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                      {skills.map(sk=>(
                        <div key={sk.id} className='skill-chip'
                          style={{ background:`${getCatColors(data.customCategories)[sk.category]||'#6366f1'}12`,
                            border:`1px solid ${getCatColors(data.customCategories)[sk.category]||'#6366f1'}30`,
                            borderRadius:8, padding:'8px 14px', cursor:'default', transition:'all 0.2s' }}>
                          <div style={{ fontSize:13,fontWeight:600,color:getCatColors(data.customCategories)[sk.category]||'#6366f1' }}>{sk.name}</div>
                          {sk.description&&<div style={{ fontSize:11,color:'#64748b',marginTop:2 }}>{sk.description}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
      {showEdit && <AddStaffModal data={data} update={update} onClose={()=>setShowEdit(false)} editingStaff={s} />}
    </div>
  );
}

// CREATE / EDIT TASK MODAL
function TaskModal({ data, update, onClose, editingTask=null }) {
  const [form, setForm] = useState(editingTask || {
    title:'', description:'', dueDate:'', category:'', skillIds:[], assignedStaffIds:[], trackerIds:[]
  });
  const [skillSearch, setSkillSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [trackerSearch, setTrackerSearch] = useState('');
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const toggleArr = (k,v) => setForm(p=>({
    ...p, [k]: p[k].includes(v) ? p[k].filter(x=>x!==v) : [...p[k],v]
  }));

  const filteredSkills = data.skills.filter(sk=>
    !skillSearch||sk.name.toLowerCase().includes(skillSearch.toLowerCase())||sk.category.toLowerCase().includes(skillSearch.toLowerCase())
  );
  const filteredStaff = data.staff.filter(s=>
    !staffSearch||s.name.toLowerCase().includes(staffSearch.toLowerCase())||s.position.toLowerCase().includes(staffSearch.toLowerCase())
  );
  const filteredTrackers = data.staff.filter(s=>
    !trackerSearch||s.name.toLowerCase().includes(trackerSearch.toLowerCase())
  );

  const save = () => {
    if(!form.title.trim()) return alert('Task title is required.');
    if(!form.dueDate) return alert('Due date is required.');
    if(editingTask) {
      update(prev=>({ tasks:prev.tasks.map(t=>t.id===editingTask.id?{...t,...form}:t) }));
    } else {
      const newTask = { ...form, id:uid(), createdAt:today() };
      update(prev=>({ tasks:[...prev.tasks,newTask] }));
    }
    onClose();
  };

  const skillsByCategory = filteredSkills.reduce((acc,sk)=>({...acc,[sk.category]:[...(acc[sk.category]||[]),sk]}),{});

  return (
    <Modal title={editingTask?'Edit PD Task':'Create PD Task'} onClose={onClose} width={720}>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px' }}>
        <div style={{ gridColumn:'1/-1' }}>
          <FInput label='Task Title' required value={form.title} onChange={e=>set('title',e.target.value)} placeholder='e.g. Complete Workplace Safety Training' />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <FTextarea label='Description' value={form.description} onChange={e=>set('description',e.target.value)} placeholder='Describe what staff need to do----' />
        </div>
        <FInput label='Due Date' required type='date' value={form.dueDate} onChange={e=>set('dueDate',e.target.value)} />
        <FSelect label='Category' value={form.category} onChange={e=>set('category',e.target.value)}>
          <option value=''>Select category----</option>
          {TASK_CATS.map(c=><option key={c} value={c}>{c}</option>)}
        </FSelect>
      </div>

      {/* Skills */}
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>
          Skills Awarded on Completion ({form.skillIds.length} selected)
        </label>
        <input value={skillSearch} onChange={e=>setSkillSearch(e.target.value)} placeholder='Search skills----'
          style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'7px 10px',fontSize:13,
            background:'#f8fafc',marginBottom:8 }} />
        <div style={{ border:'1px solid #e2e8f0',borderRadius:8,maxHeight:200,overflowY:'auto',padding:'8px' }}>
          {Object.entries(skillsByCategory).map(([cat,skills])=>(
            <div key={cat} style={{ marginBottom:10 }}>
              <div style={{ fontSize:11,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5 }}>{cat}</div>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                {skills.map(sk=>{
                  const sel = form.skillIds.includes(sk.id);
                  const c = getCatColors(data.customCategories)[sk.category]||'#6366f1';
                  return (
                    <button key={sk.id} onClick={()=>toggleArr('skillIds',sk.id)} type='button'
                      style={{ padding:'4px 10px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
                        background:sel?`${c}20`:'#f8fafc', color:sel?c:'#64748b',
                        border:sel?`1px solid ${c}50`:'1px solid #e2e8f0', transition:'all 0.15s' }}>
                      {sel?'---- ':''}{sk.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assign Staff */}
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>
          Assign To Staff ({form.assignedStaffIds.length} selected)
        </label>
        <input value={staffSearch} onChange={e=>setStaffSearch(e.target.value)} placeholder='Search staff----'
          style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'7px 10px',fontSize:13,
            background:'#f8fafc',marginBottom:8 }} />
        <div style={{ border:'1px solid #e2e8f0',borderRadius:8,maxHeight:160,overflowY:'auto' }}>
          {data.staff.length===0 ? (
            <div style={{ padding:16,textAlign:'center',color:'#94a3b8',fontSize:13 }}>No staff added yet</div>
          ) : filteredStaff.map(s=>{
            const sel = form.assignedStaffIds.includes(s.id);
            return (
              <div key={s.id} onClick={()=>toggleArr('assignedStaffIds',s.id)}
                style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 12px',cursor:'pointer',
                  background:sel?'#eef2ff':'transparent',borderBottom:'1px solid #f1f5f9' }}
                onMouseEnter={e=>!sel&&(e.currentTarget.style.background='#f8fafc')}
                onMouseLeave={e=>!sel&&(e.currentTarget.style.background='transparent')}>
                <Avatar name={s.name} size={28} />
                <div style={{ flex:1 }}><span style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{s.name}</span> <span style={{ fontSize:12,color:'#64748b' }}>- {s.position}</span></div>
                {sel&&<span style={{ color:'#6366f1',fontWeight:700,fontSize:16 }}>----</span>}
              </div>
            );
          })}
        </div>
        {data.staff.length>0&&(
          <div style={{ display:'flex',gap:8,marginTop:6 }}>
            <button type='button' onClick={()=>set('assignedStaffIds',data.staff.map(s=>s.id))}
              style={{ fontSize:12,color:'#6366f1',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit',fontWeight:600 }}>
              Select All
            </button>
            <span style={{ color:'#cbd5e1' }}>-</span>
            <button type='button' onClick={()=>set('assignedStaffIds',[])}
              style={{ fontSize:12,color:'#64748b',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit' }}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Trackers */}
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>
          Assigned Trackers / Managers ({form.trackerIds.length} selected)
          <span style={{ fontSize:11,color:'#94a3b8',fontWeight:400 }}> ------ who can monitor this task</span>
        </label>
        <div style={{ border:'1px solid #e2e8f0',borderRadius:8,maxHeight:120,overflowY:'auto' }}>
          {data.staff.filter(s=>s.role==='manager'||s.role==='admin').length===0 ? (
            <div style={{ padding:12,color:'#94a3b8',fontSize:13 }}>No managers/admins in system yet. You can still track tasks from the dashboard.</div>
          ) : data.staff.filter(s=>s.role==='manager'||s.role==='admin').map(s=>{
            const sel = form.trackerIds.includes(s.id);
            return (
              <div key={s.id} onClick={()=>toggleArr('trackerIds',s.id)}
                style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 12px',cursor:'pointer',
                  background:sel?'#f0fdf4':'transparent',borderBottom:'1px solid #f1f5f9' }}>
                <Avatar name={s.name} size={26} />
                <div style={{ flex:1 }}><span style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{s.name}</span> <Badge label={s.role} color='#10b981' sm /></div>
                {sel&&<span style={{ color:'#10b981',fontWeight:700 }}>----</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
        <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
        <Btn onClick={save}>{editingTask?'Save Changes':'Create Task'}</Btn>
      </div>
    </Modal>
  );
}

// PD TASKS LIST
function TasksList({ data, update, nav }) {
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');

  const filtered = data.tasks.filter(t=>
    (!search||t.title.toLowerCase().includes(search.toLowerCase()))
    &&(filterCat==='all'||t.category===filterCat)
  );

  const del = id => {
    if(window.confirm('Delete this task and all progress records?'))
      update(prev=>({
        tasks:prev.tasks.filter(t=>t.id!==id),
        progress:Object.fromEntries(Object.entries(prev.progress).map(([sid,map])=>[sid,
          Object.fromEntries(Object.entries(map).filter(([tid])=>tid!==id))
        ]))
      }));
  };

  const getTaskStats = (t) => {
    const total = t.assignedStaffIds.length;
    const done = t.assignedStaffIds.filter(sid=>getProgress(data.progress,sid,t.id).status==='completed').length;
    const inProg = t.assignedStaffIds.filter(sid=>getProgress(data.progress,sid,t.id).status==='in_progress').length;
    return { total, done, inProg, notStarted:total-done-inProg };
  };

  return (
    <div className='fade-in'>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:26,fontWeight:800,color:'#1e293b',margin:0 }}>PD Tasks</h1>
          <p style={{ color:'#64748b',marginTop:4,fontSize:14 }}>{data.tasks.length} task{data.tasks.length!==1?'s':''} created</p>
        </div>
        <Btn onClick={()=>setShowCreate(true)}>+ Create Task</Btn>
      </div>

      {/* Filters */}
      <div style={{ display:'flex',gap:12,marginBottom:20,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:240 }}>
          <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#94a3b8' }}>--</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder='Search tasks...'
            style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'9px 12px 9px 36px',
              fontSize:14,background:'#fff',color:'#1e293b' }} />
        </div>
        <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
          style={{ border:'1px solid #e2e8f0',borderRadius:8,padding:'9px 14px',fontSize:14,background:'#fff',cursor:'pointer',color:'#1e293b' }}>
          <option value='all'>All Categories</option>
          {TASK_CATS.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length===0 ? (
        <div className='card'>
          <EmptyState icon='' title={data.tasks.length===0?'No tasks yet':'No matching tasks'}
            desc={data.tasks.length===0?'Create your first professional development task and assign it to staff members':'Adjust your search or filter'}
            action={data.tasks.length===0&&<Btn onClick={()=>setShowCreate(true)}>+ Create First Task</Btn>} />
        </div>
      ) : (
        <div style={{ display:'grid',gap:14 }}>
          {filtered.sort((a,b)=>new Date(a.dueDate)-new Date(b.dueDate)).map(t=>{
            const stats = getTaskStats(t);
            const od = isOverdue(t.dueDate) && stats.done<stats.total;
            const pct = stats.total>0?Math.round((stats.done/stats.total)*100):0;
            const taskSkills = data.skills.filter(sk=>t.skillIds.includes(sk.id));
            return (
              <div key={t.id} className='card' style={{ padding:20,borderLeft:`4px solid ${od?'#ef4444':pct===100?'#10b981':'#6366f1'}` }}>
                <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:6 }}>
                      <h3 style={{ fontSize:15,fontWeight:700,color:'#1e293b',margin:0 }}>{t.title}</h3>
                      {t.category&&<Badge label={t.category} color='#8b5cf6' />}
                      {od&&<Badge label='-- Overdue' color='#ef4444' />}
                      {pct===100&&<Badge label='---- All Done' color='#10b981' />}
                    </div>
                    {t.description&&<p style={{ fontSize:13,color:'#64748b',margin:'0 0 10px' }}>{t.description}</p>}
                    
                    {/* Progress Bar */}
                    {stats.total>0 && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748b',marginBottom:4 }}>
                          <span>{stats.done} of {stats.total} completed</span>
                          <span style={{ fontWeight:600 }}>{pct}%</span>
                        </div>
                        <div style={{ height:6,background:'#f1f5f9',borderRadius:3,overflow:'hidden' }}>
                          <div style={{ height:'100%',width:`${pct}%`,background:pct===100?'#10b981':'#6366f1',borderRadius:3 }} />
                        </div>
                        <div style={{ display:'flex',gap:12,marginTop:6,fontSize:11,color:'#94a3b8' }}>
                          <span>---- {stats.done} done</span>
                          <span>-- {stats.inProg} in progress</span>
                          <span>-- {stats.notStarted} not started</span>
                        </div>
                      </div>
                    )}

                    <div style={{ display:'flex',alignItems:'center',gap:16,flexWrap:'wrap' }}>
                      <span style={{ fontSize:12,color:od?'#ef4444':'#64748b',fontWeight:od?700:400 }}>
                         Due {fmtDate(t.dueDate)}
                      </span>
                      <span style={{ fontSize:12,color:'#64748b' }}> {stats.total} assigned</span>
                      {taskSkills.length>0&&(
                        <div style={{ display:'flex',gap:4,flexWrap:'wrap' }}>
                          {taskSkills.slice(0,3).map(sk=>(
                            <span key={sk.id} style={{ fontSize:11,padding:'1px 7px',borderRadius:20,fontWeight:600,
                              background:`${getCatColors(data.customCategories)[sk.category]||'#6366f1'}15`,
                              color:getCatColors(data.customCategories)[sk.category]||'#6366f1',
                              border:`1px solid ${getCatColors(data.customCategories)[sk.category]||'#6366f1'}30` }}>
                              ---- {sk.name}
                            </span>
                          ))}
                          {taskSkills.length>3&&<span style={{ fontSize:11,color:'#94a3b8' }}>+{taskSkills.length-3}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display:'flex',gap:8,flexShrink:0 }}>
                    <Btn size='sm' variant='ghost' onClick={()=>nav('task-detail',t.id)}>View Progress</Btn>
                    <Btn size='sm' variant='danger' onClick={()=>del(t.id)}>Delete</Btn>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showCreate && <TaskModal data={data} update={update} onClose={()=>setShowCreate(false)} />}
    </div>
  );
}

// TASK DETAIL
function TaskDetail({ taskId, data, update, nav }) {
  const [showEdit, setShowEdit] = useState(false);
  const [search, setSearch] = useState('');
  const t = data.tasks.find(x=>x.id===taskId);
  if(!t) return <div style={{ padding:40,color:'#64748b' }}>Task not found.</div>;

  const taskSkills = data.skills.filter(sk=>t.skillIds.includes(sk.id));
  const assigned = t.assignedStaffIds.map(sid=>data.staff.find(s=>s.id===sid)).filter(Boolean);
  const filteredAssigned = assigned.filter(s=>
    !search||s.name.toLowerCase().includes(search.toLowerCase())||s.position.toLowerCase().includes(search.toLowerCase())
  );

  const done = assigned.filter(s=>getProgress(data.progress,s.id,t.id).status==='completed').length;
  const pct = assigned.length>0?Math.round((done/assigned.length)*100):0;
  const od = isOverdue(t.dueDate);

  const updateStatus = (staffId, status) => {
    const curr = getProgress(data.progress,staffId,t.id);
    update(prev=>({ progress:setProgress(prev.progress,staffId,t.id,{
      ...curr, status, completedDate:status==='completed'?today():null
    })}));
  };

  const markAll = (status) => {
    let prog = data.progress;
    t.assignedStaffIds.forEach(sid=>{
      const curr = getProgress(prog,sid,t.id);
      prog = setProgress(prog,sid,t.id,{ ...curr,status,completedDate:status==='completed'?today():null });
    });
    update(()=>({ progress:prog }));
  };

  return (
    <div className='fade-in'>
      <button onClick={()=>nav('tasks')} className='btn-anim'
        style={{ background:'none',border:'none',cursor:'pointer',color:'#6366f1',fontSize:13,fontWeight:600,
          display:'flex',alignItems:'center',gap:6,marginBottom:20,fontFamily:'inherit' }}>
        ---- Back to Tasks
      </button>

      {/* Task Header */}
      <div className='card' style={{ padding:24,marginBottom:20 }}>
        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap' }}>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:8 }}>
              <h2 style={{ fontSize:20,fontWeight:800,color:'#1e293b',margin:0 }}>{t.title}</h2>
              {t.category&&<Badge label={t.category} color='#8b5cf6' />}
              {od&&done<assigned.length&&<Badge label='-- Overdue' color='#ef4444' />}
            </div>
            {t.description&&<p style={{ color:'#64748b',fontSize:14,marginBottom:12 }}>{t.description}</p>}
            <div style={{ display:'flex',gap:20,fontSize:13,color:'#64748b',flexWrap:'wrap' }}>
              <span> Due: <strong style={{ color:od?'#ef4444':'#1e293b' }}>{fmtDate(t.dueDate)}</strong></span>
              <span> {assigned.length} assigned</span>
              <span>---- {done} completed</span>
              {taskSkills.length>0&&<span>---- {taskSkills.length} skills awarded</span>}
            </div>
          </div>
          <Btn variant='secondary' size='sm' onClick={()=>setShowEdit(true)}>---- Edit Task</Btn>
        </div>

        {/* Progress */}
        {assigned.length>0&&(
          <div style={{ marginTop:20,padding:'16px 0 0',borderTop:'1px solid #f1f5f9' }}>
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6 }}>
              <span style={{ color:'#64748b' }}>Overall Progress</span>
              <span style={{ fontWeight:700,color:'#1e293b' }}>{pct}%</span>
            </div>
            <div style={{ height:8,background:'#f1f5f9',borderRadius:4,overflow:'hidden' }}>
              <div style={{ height:'100%',width:`${pct}%`,background:pct===100?'#10b981':'#6366f1',borderRadius:4,transition:'width 0.4s' }} />
            </div>
          </div>
        )}

        {/* Skills */}
        {taskSkills.length>0&&(
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:12,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8 }}>Skills Awarded on Completion</div>
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {taskSkills.map(sk=>(
                <span key={sk.id} style={{ fontSize:12,padding:'4px 12px',borderRadius:20,fontWeight:600,
                  background:`${getCatColors(data.customCategories)[sk.category]||'#6366f1'}15`,
                  color:getCatColors(data.customCategories)[sk.category]||'#6366f1',
                  border:`1px solid ${getCatColors(data.customCategories)[sk.category]||'#6366f1'}30` }}>
                  ---- {sk.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Staff Progress */}
      <div className='card' style={{ overflow:'hidden' }}>
        <div style={{ padding:'16px 20px',borderBottom:'1px solid #e2e8f0',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12 }}>
          <h3 style={{ fontSize:15,fontWeight:700,color:'#1e293b',margin:0 }}>Staff Progress ({assigned.length})</h3>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder='Search staff----'
              style={{ border:'1px solid #e2e8f0',borderRadius:7,padding:'6px 10px',fontSize:13,background:'#f8fafc',color:'#1e293b' }} />
            <Btn size='sm' variant='success' onClick={()=>markAll('completed')}>---- Mark All Done</Btn>
            <Btn size='sm' variant='secondary' onClick={()=>markAll('not_started')}>Reset All</Btn>
          </div>
        </div>

        {assigned.length===0 ? (
          <EmptyState icon='' title='No staff assigned' desc='Edit this task to assign staff members' />
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc',borderBottom:'1px solid #e2e8f0' }}>
                {['Staff Member','Status','Completed','Actions'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px',textAlign:'left',fontSize:11,
                    fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAssigned.map((s,i)=>{
                const p = getProgress(data.progress,s.id,t.id);
                const od_s = isOverdue(t.dueDate)&&p.status!=='completed';
                return (
                  <tr key={s.id} className='row-hover' style={{ borderBottom:i<filteredAssigned.length-1?'1px solid #f1f5f9':'none' }}>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <Avatar name={s.name} size={32} />
                        <div>
                          <div style={{ fontSize:13,fontWeight:600,color:'#1e293b' }}>{s.name}</div>
                          <div style={{ fontSize:11,color:'#94a3b8' }}>{s.position}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <StatusBadge status={p.status} />
                        {od_s&&<Badge label='Overdue' color='#ef4444' />}
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px',fontSize:12,color:p.completedDate?'#10b981':'#94a3b8',fontWeight:p.completedDate?600:400 }}>
                      {p.completedDate?fmtDate(p.completedDate):'------'}
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                        {p.status==='not_started'&&<Btn size='sm' variant='secondary' onClick={()=>updateStatus(s.id,'in_progress')}>Start</Btn>}
                        {p.status!=='completed'&&<Btn size='sm' variant='success' onClick={()=>updateStatus(s.id,'completed')}>---- Done</Btn>}
                        {p.status==='completed'&&<Btn size='sm' variant='secondary' onClick={()=>updateStatus(s.id,'not_started')}>Reset</Btn>}
                        {p.status==='in_progress'&&<Btn size='sm' variant='danger' onClick={()=>updateStatus(s.id,'not_started')}>Stop</Btn>}
                        <Btn size='sm' variant='ghost' onClick={()=>nav('staff-profile',s.id)}>Profile</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {showEdit&&<TaskModal data={data} update={update} onClose={()=>setShowEdit(false)} editingTask={t} />}
    </div>
  );
}


// SKILLS REPOSITORY -- 3-LEVEL HIERARCHY -- Master Category > Sub-Category > Skills


const PRESET_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#1d4ed8','#059669','#7c3aed'];

const OU_LEVELS = [
  { label:'Leading Self',          cats:['Professional Soft Skills','Personal Effectiveness'],                                    color:'#14b8a6' },
  { label:'Leading Others',        cats:['Business Acumen','Collaborating with Others','Role-Specific Technical/Functional'],     color:'#6366f1' },
  { label:'Leading Teams',         cats:['Leadership Soft Skills','Managing People'],                                             color:'#f97316' },
  { label:'Leading Departments',   cats:['Managing Teams','Department Leadership'],                                               color:'#10b981' },
  { label:'Leading Organizations', cats:['Leading Division','Business Results','Client Community'],                               color:'#1d4ed8' },
];

/* Add Master Category Modal */
function AddMasterCategoryModal({ update, onClose }) {
  const [form, setForm] = useState({ name:'', color:'#6366f1' });
  const save = () => {
    if(!form.name.trim()) return alert('Name is required.');
    update(prev=>({
      masterCategories:[...(prev.masterCategories||[]),{ id:uid(), name:form.name.trim(), color:form.color, isBuiltIn:false }]
    }));
    onClose();
  };
  return (
    <Modal title='Create Master Category' onClose={onClose} width={440}>
      <FInput label='Master Category Name' required value={form.name}
        onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder='e.g. NCSY Staff Training, Leadership Academy----' />
      <div style={{ marginBottom:20 }}>
        <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>Color</label>
        <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
          {PRESET_COLORS.map(c=>(
            <button key={c} type='button' onClick={()=>setForm(p=>({...p,color:c}))}
              style={{ width:30,height:30,borderRadius:'50%',background:c,cursor:'pointer',
                border:form.color===c?'3px solid #1e293b':'2px solid transparent',
                transform:form.color===c?'scale(1.2)':'scale(1)',transition:'all 0.1s' }} />
          ))}
          <input type='color' value={form.color} onChange={e=>setForm(p=>({...p,color:e.target.value}))}
            style={{ width:34,height:34,borderRadius:6,border:'1px solid #e2e8f0',padding:2,cursor:'pointer' }} />
        </div>
      </div>
      {form.name&&(
        <div style={{ background:'#f8fafc',borderRadius:8,padding:'10px 14px',marginBottom:16,
          display:'flex',alignItems:'center',gap:10 }}>
          <div style={{ width:14,height:14,borderRadius:3,background:form.color,flexShrink:0 }}/>
          <span style={{ fontSize:14,fontWeight:700,color:'#1e293b' }}>{form.name}</span>
          <Badge label='Preview' color={form.color} sm />
        </div>
      )}
      <div style={{ display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
        <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
        <Btn onClick={save}>Create Master Category</Btn>
      </div>
    </Modal>
  );
}

/* Add Sub-Category Modal */
function AddSubCategoryModal({ data, update, defaultMasterId, onClose }) {
  const masters = data.masterCategories||[];
  const [form, setForm] = useState({ name:'', color:'#6366f1', masterCategoryId: defaultMasterId||masters[0]?.id||'' });
  const save = () => {
    if(!form.name.trim()) return alert('Category name is required.');
    const master = masters.find(m=>m.id===form.masterCategoryId);
    if(!master) return alert('Select a master category.');
    const exists = (data.customCategories||[]).some(c=>c.name===form.name.trim()&&c.masterCategoryId===form.masterCategoryId)
      || Object.keys(CAT_COLORS).includes(form.name.trim());
    if(exists) return alert('A category with that name already exists.');
    update(prev=>({ customCategories:[...(prev.customCategories||[]),{
      name:form.name.trim(), color:form.color, masterCategoryId:form.masterCategoryId
    }]}));
    onClose();
  };
  return (
    <Modal title='Add Sub-Category' onClose={onClose} width={460}>
      <FSelect label='Master Category' required value={form.masterCategoryId} onChange={e=>setForm(p=>({...p,masterCategoryId:e.target.value}))}>
        {masters.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
      </FSelect>
      <FInput label='Sub-Category Name' required value={form.name}
        onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder='e.g. Digital Skills, Ethics----' />
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:8 }}>Color</label>
        <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
          {PRESET_COLORS.map(c=>(
            <button key={c} type='button' onClick={()=>setForm(p=>({...p,color:c}))}
              style={{ width:28,height:28,borderRadius:'50%',background:c,cursor:'pointer',
                border:form.color===c?'3px solid #1e293b':'2px solid transparent',
                transform:form.color===c?'scale(1.15)':'scale(1)',transition:'all 0.1s' }} />
          ))}
          <input type='color' value={form.color} onChange={e=>setForm(p=>({...p,color:e.target.value}))}
            style={{ width:32,height:32,borderRadius:6,border:'1px solid #e2e8f0',padding:2,cursor:'pointer' }} />
        </div>
      </div>
      <div style={{ display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
        <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
        <Btn onClick={save}>Add Sub-Category</Btn>
      </div>
    </Modal>
  );
}

/* Add Skill Modal */
function AddSkillModal({ data, update, defaultMasterId, onClose }) {
  const masters = data.masterCategories||[];
  const [masterId, setMasterId] = useState(defaultMasterId||masters[0]?.id||'');

  const getMasterName = id => (masters.find(m=>m.id===id)||{}).name||'';
  const masterName = getMasterName(masterId);
  const isOU = masterId === 'mc_ou';

  // Gather sub-categories available for this master
  const builtInCats = isOU ? Object.keys(CAT_COLORS).filter(c=>c!=='Custom') : [];
  const customCats = (data.customCategories||[])
    .filter(c=>c.masterCategoryId===masterId)
    .map(c=>c.name);
  const allCats = [...builtInCats, ...customCats];

  const allCatColors = getCatColors(data.customCategories);

  const [form, setForm] = useState({ name:'', category: allCats[0]||'', description:'' });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // When master changes, reset category
  const handleMasterChange = id => {
    setMasterId(id);
    const mName = getMasterName(id);
    const isOUNew = id==='mc_ou';
    const builtIn = isOUNew ? Object.keys(CAT_COLORS).filter(c=>c!=='Custom') : [];
    const custom = (data.customCategories||[]).filter(c=>c.masterCategoryId===id).map(c=>c.name);
    setForm(p=>({...p, category:[...builtIn,...custom][0]||''}));
  };

  const save = () => {
    if(!form.name.trim()) return alert('Skill name is required.');
    if(!form.category) return alert('Please select a sub-category.');
    update(prev=>({ skills:[...prev.skills,{
      ...form, id:uid(), isCustom:true, masterCategory:masterName
    }]}));
    onClose();
  };

  return (
    <Modal title='Add Custom Skill' onClose={onClose} width={520}>
      <FSelect label='Master Category' required value={masterId} onChange={e=>handleMasterChange(e.target.value)}>
        {masters.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
      </FSelect>
      <FSelect label='Sub-Category' required value={form.category} onChange={e=>set('category',e.target.value)}>
        <option value=''>Select sub-category----</option>
        {allCats.map(c=><option key={c} value={c}>{c}</option>)}
        {allCats.length===0&&<option disabled>No sub-categories yet ------ add one first</option>}
      </FSelect>
      <FInput label='Skill Name' required value={form.name} onChange={e=>set('name',e.target.value)} placeholder='e.g. Agile Methodology' />
      <FTextarea label='Description' value={form.description} onChange={e=>set('description',e.target.value)} placeholder='Brief description of this skill----' />
      <div style={{ display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9' }}>
        <Btn variant='secondary' onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={!form.category}>Add Skill</Btn>
      </div>
    </Modal>
  );
}

/* Skills Repository Main Component */
function SkillsRepository({ data, update }) {
  const [activeMaster, setActiveMaster] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showAddMaster, setShowAddMaster] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [showAddCat, setShowAddCat] = useState(false);

  const allCatColors = getCatColors(data.customCategories);
  const masters = data.masterCategories||[];

  // Skill earned count
  const skillEarnedCount = {};
  data.staff.forEach(s=>data.tasks.forEach(t=>{
    if(!t.assignedStaffIds.includes(s.id)) return;
    if(getProgress(data.progress,s.id,t.id).status==='completed')
      t.skillIds.forEach(sid=>{ skillEarnedCount[sid]=(skillEarnedCount[sid]||0)+1; });
  }));

  const delSkill = id => {
    if(window.confirm('Remove this skill?'))
      update(prev=>({
        skills:prev.skills.filter(s=>s.id!==id),
        tasks:prev.tasks.map(t=>({...t,skillIds:t.skillIds.filter(sid=>sid!==id)}))
      }));
  };

  const delMaster = id => {
    const mc = masters.find(m=>m.id===id);
    if(!mc||mc.isBuiltIn) return;
    const skillCount = data.skills.filter(s=>s.masterCategory===mc.name).length;
    if(!window.confirm(`Delete "${mc.name}"? ${skillCount>0?`${skillCount} skills will also be removed.`:''}`.trim())) return;
    update(prev=>({
      masterCategories:prev.masterCategories.filter(m=>m.id!==id),
      customCategories:(prev.customCategories||[]).filter(c=>c.masterCategoryId!==id),
      skills:prev.skills.filter(s=>s.masterCategory!==mc.name),
    }));
    setActiveMaster(null);
  };

  const delSubCat = name => {
    if(!window.confirm(`Delete sub-category "${name}"? Skills in it will also be removed.`)) return;
    update(prev=>({
      customCategories:(prev.customCategories||[]).filter(c=>c.name!==name),
      skills:prev.skills.filter(s=>s.category!==name),
      tasks:prev.tasks.map(t=>({...t,skillIds:t.skillIds.filter(sid=>!prev.skills.find(sk=>sk.id===sid&&sk.category===name))}))
    }));
  };

  /* -- MASTER CATEGORY GRID VIEW -------------------------------------------------------------------------------------------------------------------------------- */
  if (!activeMaster) {
    return (
      <div className='fade-in'>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:26,fontWeight:800,color:'#1e293b',margin:0 }}>Skills Library</h1>
            <p style={{ color:'#64748b',marginTop:4,fontSize:14 }}>{masters.length} master categories - {data.skills.length} total skills</p>
          </div>
          <Btn onClick={()=>setShowAddMaster(true)}>+ New Master Category</Btn>
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:20 }}>
          {masters.map(mc=>{
            const mcSkills = data.skills.filter(s=>s.masterCategory===mc.name);
            const cats = [...new Set(mcSkills.map(s=>s.category))];
            const isOU = mc.id==='mc_ou';
            return (
              <div key={mc.id} className='card skill-chip' onClick={()=>{ setActiveMaster(mc); setFilterCat('all'); setSearch(''); }}
                style={{ padding:0,overflow:'hidden',cursor:'pointer',transition:'all 0.2s',border:`1px solid ${mc.color}30` }}>
                {/* Color Bar */}
                <div style={{ height:6,background:`linear-gradient(90deg,${mc.color},${mc.color}88)` }} />
                <div style={{ padding:20 }}>
                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:16,fontWeight:800,color:'#1e293b',fontFamily:'Sora,sans-serif',marginBottom:4 }}>{mc.name}</div>
                      {mc.isBuiltIn&&<Badge label='Built-in' color='#64748b' sm />}
                    </div>
                    {!mc.isBuiltIn&&(
                      <button onClick={e=>{e.stopPropagation();delMaster(mc.id);}}
                        style={{ background:'none',border:'none',cursor:'pointer',color:'#cbd5e1',fontSize:16,padding:'2px 4px',flexShrink:0 }}
                        onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
                        onMouseLeave={e=>e.currentTarget.style.color='#cbd5e1'}>----</button>
                    )}
                  </div>

                  <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16 }}>
                    {[
                      { label:'Skills',      val:mcSkills.length, icon:'----' },
                      { label:'Categories',  val:cats.length,     icon:'*' },
                    ].map(s=>(
                      <div key={s.label} style={{ background:'#f8fafc',borderRadius:8,padding:'10px 12px',textAlign:'center' }}>
                        <div style={{ fontSize:20,marginBottom:2 }}>{s.icon}</div>
                        <div style={{ fontSize:20,fontWeight:800,color:mc.color,fontFamily:'Sora,sans-serif',lineHeight:1 }}>{s.val}</div>
                        <div style={{ fontSize:11,color:'#94a3b8',marginTop:2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {isOU && (
                    <div style={{ display:'flex',flexWrap:'wrap',gap:4,marginBottom:14 }}>
                      {OU_LEVELS.map(l=>(
                        <span key={l.label} style={{ fontSize:10,padding:'2px 7px',borderRadius:20,fontWeight:600,
                          background:`${l.color}15`,color:l.color,border:`1px solid ${l.color}30` }}>{l.label}</span>
                      ))}
                    </div>
                  )}

                  {!isOU && cats.length>0 && (
                    <div style={{ display:'flex',flexWrap:'wrap',gap:4,marginBottom:14 }}>
                      {cats.slice(0,5).map(c=>{
                        const col = allCatColors[c]||'#6366f1';
                        return <span key={c} style={{ fontSize:10,padding:'2px 7px',borderRadius:20,fontWeight:600,
                          background:`${col}15`,color:col,border:`1px solid ${col}30` }}>{c}</span>;
                      })}
                      {cats.length>5&&<span style={{ fontSize:10,color:'#94a3b8' }}>+{cats.length-5} more</span>}
                    </div>
                  )}

                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                    <span style={{ fontSize:12,color:'#94a3b8' }}>Click to browse skills</span>
                    <span style={{ fontSize:18,color:mc.color }}>--</span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Master Category Card */}
          <div onClick={()=>setShowAddMaster(true)} className='btn-anim'
            style={{ border:'2px dashed #e2e8f0',borderRadius:12,padding:28,cursor:'pointer',
              display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
              gap:10,minHeight:180,transition:'border-color 0.2s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor='#6366f1'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='#e2e8f0'}>
            <div style={{ width:48,height:48,borderRadius:12,background:'#eef2ff',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:22 }}>--</div>
            <div style={{ fontSize:14,fontWeight:700,color:'#6366f1' }}>New Master Category</div>
            <div style={{ fontSize:12,color:'#94a3b8',textAlign:'center' }}>Create a custom category group for your training programs</div>
          </div>
        </div>

        {showAddMaster&&<AddMasterCategoryModal update={update} onClose={()=>setShowAddMaster(false)} />}
      </div>
    );
  }

  /* -- DRILLED-IN VIEW-------------- */
  const isOU = activeMaster.id==='mc_ou';
  const color = activeMaster.color;

  // All sub-categories for this master
  const builtInSubCats = isOU ? Object.keys(CAT_COLORS).filter(c=>c!=='Custom') : [];
  const customSubCats = (data.customCategories||[]).filter(c=>c.masterCategoryId===activeMaster.id);
  const allSubCatNames = [...builtInSubCats, ...customSubCats.map(c=>c.name)];

  // Filtered skills
  const masterSkills = data.skills.filter(s=>
    s.masterCategory===activeMaster.name &&
    (!search||s.name.toLowerCase().includes(search.toLowerCase())||(s.description||'').toLowerCase().includes(search.toLowerCase())) &&
    (filterCat==='all'||s.category===filterCat)
  );
  const bySubCat = masterSkills.reduce((acc,sk)=>({...acc,[sk.category]:[...(acc[sk.category]||[]),sk]}),{});

  return (
    <div className='fade-in'>
      {/* Breadcrumb */}
      <button onClick={()=>setActiveMaster(null)} className='btn-anim'
        style={{ background:'none',border:'none',cursor:'pointer',color:'#6366f1',fontSize:13,fontWeight:600,
          display:'flex',alignItems:'center',gap:6,marginBottom:20,fontFamily:'inherit' }}>
        ---- Skills Library
      </button>

      {/* Header */}
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12 }}>
        <div style={{ display:'flex',alignItems:'center',gap:14 }}>
          <div style={{ width:48,height:48,borderRadius:12,background:`${color}18`,
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,border:`1px solid ${color}30` }}></div>
          <div>
            <h1 style={{ fontSize:22,fontWeight:800,color:'#1e293b',margin:0 }}>{activeMaster.name}</h1>
            <p style={{ color:'#64748b',margin:'4px 0 0',fontSize:13 }}>
              {masterSkills.length} of {data.skills.filter(s=>s.masterCategory===activeMaster.name).length} skills shown - {allSubCatNames.length} sub-categories
            </p>
          </div>
        </div>
        <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
          <Btn variant='secondary' size='sm' onClick={()=>setShowAddCat(true)}>+ Add Sub-Category</Btn>
          <Btn size='sm' onClick={()=>setShowAddSkill(true)}>+ Add Skill</Btn>
        </div>
      </div>

      {/* OU Level Legend */}
      {isOU && (
        <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:16 }}>
          {OU_LEVELS.map(l=>(
            <div key={l.label} style={{ background:`${l.color}12`,border:`1px solid ${l.color}30`,borderRadius:8,padding:'5px 10px',fontSize:12 }}>
              <span style={{ fontWeight:700,color:l.color }}>{l.label}</span>
              <span style={{ color:'#94a3b8',marginLeft:6 }}>{l.cats.join(' - ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex',gap:10,marginBottom:16,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:220 }}>
          <span style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'#94a3b8' }}>--</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder='Search skills...'
            style={{ width:'100%',border:'1px solid #e2e8f0',borderRadius:8,padding:'8px 12px 8px 34px',fontSize:13,background:'#fff',color:'#1e293b' }} />
        </div>
        <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
          style={{ border:'1px solid #e2e8f0',borderRadius:8,padding:'8px 12px',fontSize:13,background:'#fff',cursor:'pointer',color:'#1e293b' }}>
          <option value='all'>All Sub-Categories</option>
          {allSubCatNames.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Sub-Category Chips */}
      <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:24 }}>
        <button onClick={()=>setFilterCat('all')} className='btn-anim'
          style={{ padding:'4px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
            background:filterCat==='all'?`${color}20`:'#f8fafc',
            color:filterCat==='all'?color:'#64748b',
            border:filterCat==='all'?`1px solid ${color}50`:'1px solid #e2e8f0' }}>
          All ({data.skills.filter(s=>s.masterCategory===activeMaster.name).length})
        </button>
        {allSubCatNames.map(cat=>{
          const catColor = allCatColors[cat]||color;
          const isCustomSubCat = customSubCats.some(c=>c.name===cat);
          const count = data.skills.filter(s=>s.masterCategory===activeMaster.name&&s.category===cat).length;
          return (
            <div key={cat} style={{ display:'flex',alignItems:'center' }}>
              <button onClick={()=>setFilterCat(filterCat===cat?'all':cat)} className='btn-anim'
                style={{ padding:'4px 10px',borderRadius:isCustomSubCat?'20px 0 0 20px':'20px',fontSize:12,fontWeight:600,cursor:'pointer',
                  background:filterCat===cat?`${catColor}20`:'#f8fafc',
                  color:filterCat===cat?catColor:'#64748b',
                  border:filterCat===cat?`1px solid ${catColor}50`:'1px solid #e2e8f0',
                  borderRight:isCustomSubCat?'none':undefined }}>
                {cat} ({count})
              </button>
              {isCustomSubCat&&(
                <button onClick={()=>delSubCat(cat)} title='Delete sub-category'
                  style={{ padding:'4px 7px',borderRadius:'0 20px 20px 0',fontSize:11,cursor:'pointer',
                    background:filterCat===cat?`${catColor}20`:'#f8fafc',color:'#cbd5e1',
                    border:filterCat===cat?`1px solid ${catColor}50`:'1px solid #e2e8f0',borderLeft:'none' }}
                  onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
                  onMouseLeave={e=>e.currentTarget.style.color='#cbd5e1'}>----</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Skills */}
      {Object.keys(bySubCat).length===0 ? (
        <div className='card'>
          <EmptyState icon='----' title='No skills found'
            desc={data.skills.filter(s=>s.masterCategory===activeMaster.name).length===0
              ? 'Add sub-categories and skills to this master category'
              : 'Try adjusting your search or filter'}
            action={
              <div style={{ display:'flex',gap:10,justifyContent:'center' }}>
                <Btn variant='secondary' onClick={()=>setShowAddCat(true)}>+ Add Sub-Category</Btn>
                <Btn onClick={()=>setShowAddSkill(true)}>+ Add Skill</Btn>
              </div>
            } />
        </div>
      ) : (
        // For OU: group by level then sub-category; for custom: flat sub-category
        isOU ? (
          OU_LEVELS.map(level=>{
            const levelCats = level.cats.filter(c=>bySubCat[c]);
            if(levelCats.length===0) return null;
            return (
              <div key={level.label} style={{ marginBottom:28 }}>
                <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14,
                  padding:'8px 14px',background:`${level.color}10`,borderRadius:8,border:`1px solid ${level.color}25` }}>
                  <div style={{ width:10,height:10,borderRadius:2,background:level.color,flexShrink:0 }}/>
                  <span style={{ fontSize:13,fontWeight:800,color:level.color,fontFamily:'Sora,sans-serif' }}>{level.label}</span>
                </div>
                {levelCats.map(cat=>renderSubCatBlock(cat, bySubCat[cat], allCatColors[cat]||level.color, false))}
              </div>
            );
          })
        ) : (
          Object.entries(bySubCat).map(([cat,skills])=>
            renderSubCatBlock(cat, skills, allCatColors[cat]||color, true)
          )
        )
      )}

      {showAddMaster&&<AddMasterCategoryModal update={update} onClose={()=>setShowAddMaster(false)} />}
      {showAddCat&&<AddSubCategoryModal data={data} update={update} defaultMasterId={activeMaster.id} onClose={()=>setShowAddCat(false)} />}
      {showAddSkill&&<AddSkillModal data={data} update={update} defaultMasterId={activeMaster.id} onClose={()=>setShowAddSkill(false)} />}
    </div>
  );

  function renderSubCatBlock(cat, skills, catColor, showDelete) {
    return (
      <div key={cat} style={{ marginBottom:18 }}>
        <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
          <div style={{ width:10,height:10,borderRadius:2,background:catColor }}/>
          <span style={{ fontSize:13,fontWeight:700,color:'#1e293b' }}>{cat}</span>
          <span style={{ fontSize:11,color:'#94a3b8' }}>{skills.length} skill{skills.length!==1?'s':''}</span>
          {showDelete&&(
            <button onClick={()=>delSubCat(cat)}
              style={{ background:'none',border:'none',cursor:'pointer',color:'#e2e8f0',fontSize:13,padding:'0 4px',marginLeft:'auto' }}
              onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
              onMouseLeave={e=>e.currentTarget.style.color='#e2e8f0'}>---- Delete</button>
          )}
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))',gap:8 }}>
          {skills.map(sk=>(
            <div key={sk.id} className='card skill-chip'
              style={{ padding:14,borderLeft:`3px solid ${catColor}`,transition:'all 0.2s' }}>
              <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13,fontWeight:700,color:'#1e293b',display:'flex',alignItems:'center',gap:6,marginBottom:3 }}>
                    {sk.name}
                    {sk.isCustom&&<Badge label='Custom' color='#ec4899' sm />}
                  </div>
                  {sk.description&&<div style={{ fontSize:11,color:'#64748b',lineHeight:1.4 }}>{sk.description}</div>}
                  {skillEarnedCount[sk.id]>0&&(
                    <div style={{ fontSize:10,color:'#10b981',fontWeight:600,marginTop:5 }}>
                      ---- Earned by {skillEarnedCount[sk.id]} staff member{skillEarnedCount[sk.id]!==1?'s':''}
                    </div>
                  )}
                </div>
                {sk.isCustom&&(
                  <button onClick={()=>delSkill(sk.id)}
                    style={{ background:'none',border:'none',cursor:'pointer',color:'#cbd5e1',fontSize:15,padding:'0 2px',flexShrink:0 }}
                    onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
                    onMouseLeave={e=>e.currentTarget.style.color='#cbd5e1'}>----</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}


// MAIN APP
export default function App() {
  const [data, update] = usePlatformData();
  const [view, setView] = useState('dashboard');
  const [selectedId, setSelectedId] = useState(null);

  const nav = (v, id=null) => { setView(v); setSelectedId(id); window.scrollTo(0,0); };

  const renderView = () => {
    switch(view) {
      case 'dashboard':    return <Dashboard data={data} nav={nav} />;
      case 'staff':        return <StaffList data={data} update={update} nav={nav} />;
      case 'staff-profile':return <StaffProfile staffId={selectedId} data={data} update={update} nav={nav} />;
      case 'tasks':        return <TasksList data={data} update={update} nav={nav} />;
      case 'task-detail':  return <TaskDetail taskId={selectedId} data={data} update={update} nav={nav} />;
      case 'skills':       return <SkillsRepository data={data} update={update} />;
      default:             return <Dashboard data={data} nav={nav} />;
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />
      <div style={{ display:'flex',minHeight:'100vh' }}>
        <Sidebar view={view} nav={nav} />
        <main style={{ marginLeft:220,flex:1,padding:'32px 36px',minHeight:'100vh',maxWidth:'calc(100vw - 220px)' }}>
          <div style={{ maxWidth:1200,margin:'0 auto' }}>
            {renderView()}
          </div>
        </main>
      </div>
    </>
  );
}
